import _ from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PapsContext } from "../PapsContext";
import { createRefPath_client, createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { _appUserPresenceCollection } from 'global/cnr/reducers/AppUserReducer';
import { _showAppLogs, LogContext } from '../LogContext';
import { _cacheGroupTypes } from './useTimestamp';
import { useCachedData } from './useCachedData';
import { where, writeBatch } from 'firebase/firestore';
import { getGallery_clientProfiles } from 'global/storage/storageItems';
import { ParentContext } from '../ParentContext';
import { DatabaseContext } from '../DatabaseContext';
import { PageContext } from '../PageContext';
import { AppContext } from '../AppProvider';
import { gEnums } from 'global/enums/globalEnums';

const dbCallTypes = {
  appProfiles: 'appProfiles',
  appUsersSnapshot: 'appUsersSnapshot',
  clientProfiles: 'clientProfiles',
  appUsersPresence: 'appUsersPresence',
};

const allow_profileGallery = true

export const useAppUsersInfo = () => {

  const appContext = useContext(AppContext)
  const { app_state } = appContext ?? {}
  const { mainStorage, mainDb } = app_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext
  const { pathViews } = paps_state ?? {}
  const { events: eventKey } = pathViews ?? {}

  const [appUserSnapshot, setAppUserSnapshot] = useState()
  const [appUsersPresense, setAppUsersPresense] = useState()
  const [clientProfiles, setClientProfiles] = useState()

  const [updating, setUpdating] = useState()

  useCachedData({
    cache_groupType: _cacheGroupTypes.appUsersInfo,
    cache_key: eventKey,
    fetchers: [
      { key: dbCallTypes.appProfiles, getConfigFn: getConfig_clientProfile },
      { key: dbCallTypes.appUsersSnapshot, getConfigFn: getConfig_appUserSnapshot },
      { key: dbCallTypes.clientProfiles, getConfigFn: getConfig_clientProfileGallery },
      { key: dbCallTypes.appUsersPresence, getConfigFn: getConfig_getAppUserPresence },
    ],
    updateLocalState: (data) => {
      _.forEach(data, (value, key) => {
        switch (key) {
          case dbCallTypes.appProfiles:
            console.log('value', value)
            break;
          case dbCallTypes.appUsersSnapshot:
            setAppUserSnapshot(value);
            break;
          case dbCallTypes.clientProfiles:
            setClientProfiles(data?.clientProfiles);
            break;
          case dbCallTypes.appUsersPresence:
            setAppUsersPresense(data);
            break;
        }
      });
    }
  })


  // ------------------- Memoized stable return -------------------
  const appUsersInfo = useMemo(
    () => ({ appUserSnapshot, clientProfiles, appUsersPresense }),
    [appUserSnapshot, clientProfiles, appUsersPresense]
  );

  const createClientProfileUrls = useCallback(async (aps_global, appUserCollection, appDataDocuments, pathViews, callback) => {
    setUpdating(true)
    await handleCreateClientProfileUrls(database_fns, aps_global, appUserCollection, appDataDocuments, callback, mainStorage, mainDb, pathViews)
    setUpdating(false)
  }, [])

  const updateProfileUrls = useCallback(async ({ pathViews, image_name, uploadedBy, storageUrls }) => {
    setUpdating(true)
    await handleUpdateProfileUrls(database_fns, mainDb, pathViews, image_name, uploadedBy, storageUrls)
    setUpdating(false)
  }, [])

  return { appUsersInfo, updateProfileUrls, createClientProfileUrls, updating };

};


const getConfig_clientProfile = (pathViews, callback) => {
  const wheres = []
  wheres.push(where(`events.${pathViews.events}`, '!=', null))
  return {
    refPath: createRefPath_event(pathViews, ['profiles']),
    wheres,
    callback,
  }
};

/**
 * ['_appUserSnapshot']
 * @param {*} pathViews 
 * @param {*} callback 
 * @returns 
 */
const getConfig_appUserSnapshot = (pathViews, callback) => ({
  refPath: createRefPath_event(pathViews, ['_appUserSnapshot']),
  opts: { returnFirstObject: true, ignoreId: true, listen: true },
  callback,
});

/** 
 * @param {*} pathViews 
 * @param {*} callback 
 * @returns 
 */
const getConfig_clientProfileGallery = (pathViews, callback) => ({
  refPath: createRefPath_client(pathViews, ['profileGallery', 'galleryItems']),
  opts: { returnFirstObject: true, ignoreId: true, listen: true },
  callback,
});

const getConfig_getAppUserPresence = (pathViews, callback) => ({
  refPath: createRefPath_event(pathViews, [_appUserPresenceCollection]),
  opts: { ignoreId: true, listen: true },
  callback,
});

const handleCreateClientProfileUrls = async (database_fns, aps_global, appUserCollection, appDataDocuments, callback, mainStorage, mainDb, pathViews) => {

  const { storageOptions } = aps_global ?? {}
  const { clientStorageImageCollections } = storageOptions ?? {}
  let allEmails = []
  _.forEach(clientStorageImageCollections, (imageCollection) => {
    const appDataDocument = appDataDocuments?.[imageCollection]
    const emails = appDataDocument ? _.map(_.filter(appDataDocument, 'email'), 'email') : [];
    allEmails = _.concat(allEmails, emails)
  })

  const cb = async (result) => {
    const { galleryItems } = result ?? {}
    if (database_fns) {
      const batch = writeBatch(mainDb);
      const dataToUpdate = _.mapValues(galleryItems, ({ urls, customMetadata }) => ({
        urls,
        customMetadata,
      }));
      const reff = createRefPath_client(pathViews, ['profileGallery', 'galleryItems'])
      // update profileGallery
      //refPath, data, merge, dispatch, batch, altDb
      database_fns.set_doc(reff, dataToUpdate, false, false, batch);

      // update timestamp
      const _timestamp = Date.now()
      database_fns.update_timestamp(_cacheGroupTypes.appUsersInfo, pathViews.events, _timestamp, batch)
      // await database_fns.set_doc(reff, dataToUpdate, false)

      if (allow_profileGallery) {
        await batch.commit()
      }

      callback('done')
    }
  }

  getGallery_clientProfiles(mainStorage, pathViews, appUserCollection, null, cb, gEnums.storageLocationTypes.clientProfiles, allEmails)
}

const handleUpdateProfileUrls = async (database_fns, mainDb, pathViews, image_name, uploadedBy, storageUrls) => {

  const batch = writeBatch(mainDb);

  const refPath = createRefPath_client(pathViews, ['profileGallery', 'galleryItems'])
  const dataToUpdate = {
    [image_name]: {
      customMetadata: { uploadedBy: uploadedBy || 'admin' },
      urls: {
        full: storageUrls.full,
        thumbnail: storageUrls.thumbnail,
      },
    }
  }
  database_fns.set_doc(refPath, dataToUpdate, false, false, batch);

  // update timestamp
  const _timestamp = Date.now()
  database_fns.update_timestamp(_cacheGroupTypes.appUsersInfo, pathViews.events, _timestamp, batch)

  if (allow_profileGallery) {
    await batch.commit()
  }
}